import { jsPDF } from 'jspdf';
import { setupPDFFonts, verifyFontEmbedding } from '../../utils/pdfFonts';
import { renderSpreadToPDF } from './spreadRenderer.jsx';

/**
 * Generate a cover PDF specifically for Lulu with exact dimensions
 * @param {Object} book - Book data
 * @param {Object} options - Options for PDF generation
 * @returns {Promise<Blob>} - PDF blob
 */
export async function generateLuluCoverPDF(book, options = {}) {
  try {
    console.log('Generating Lulu-specific cover PDF with exact dimensions');
    
    if (!book || !book.spreads) {
      throw new Error('Book or spreads data is missing');
    }
    
    // Get first spread (spread-0)
    const firstSpread = book.spreads['spread-0'];
    
    if (!firstSpread) {
      throw new Error('First spread (spread-0) is missing');
    }
    
    // EXACT Lulu cover dimensions for US Trade (6x9)
    const width = 12.382 * 72;  // 891.5pt - EXACT per Lulu spec
    const height = 9.25 * 72;   // 666pt - EXACT per Lulu spec
    
    // Create a new PDF with the EXACT dimensions needed
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'pt',
      format: [width, height],
      hotfixes: ['px_scaling'], // Disable automatic scaling
      compress: true,
      putOnlyUsedFonts: true,
    });
    
    // Verify dimensions immediately
    console.log(`PDF dimensions: ${doc.internal.pageSize.getWidth()}pt × ${doc.internal.pageSize.getHeight()}pt`);
    console.log(`In inches: ${doc.internal.pageSize.getWidth()/72}" × ${doc.internal.pageSize.getHeight()/72}"`);
    
    // Setup fonts
    await setupPDFFonts(doc);
    
    // Get cover color
    const coverColor = 
      book?.coverDetails?.frontCover?.color || 
      book?.frontCoverColor || 
      '#1B5E20'; // Default to dark green if no color specified
    
    // Convert hex color to RGB for background
    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 27, g: 94, b: 32 }; // Default dark green
    };
    
    const rgb = hexToRgb(coverColor);
    
    // Set background color for the entire document
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.rect(0, 0, width, height, 'F');
    
    // Render the book content exactly as it appears in the design view, without guidelines or barcode area
    // Pass orientation information for consistent positioning across all renderers
    const orientation = 'landscape'; // Lulu cover is always landscape format
    await renderSpreadToPDF(doc, firstSpread, { 
      isCoverSpread: true,
      orientation: orientation
    });
    
    // Set PDF properties
    doc.setProperties({
      title: book?.title || 'Book Cover', 
      subject: 'Book Cover',
      creator: 'StorybookWiz',
      author: book?.author || 'Author'
    });

    // Verify font embedding
    verifyFontEmbedding(doc);
    
    // Return the PDF as a blob
    return new Blob([doc.output('arraybuffer')], { type: 'application/pdf' });
  } catch (error) {
    console.error('Error generating Lulu cover PDF:', error);
    throw error;
  }
}
